/**
 * Multi-factor auth types, mirroring the pinned D-ORCA MFA proxy contracts
 * (docs/plans/2026-07-30-better-auth-mfa-implementation-plan.md).
 */

/**
 * The challenge methods the login response can advertise via
 * `{mfaRequired: true, methods}`. Backup codes are deliberately NOT part of
 * this set — the contract's `methods` array only carries the two *push-able*
 * factors. See `challengeMethodsToOffer` for how the backup fallback is
 * derived.
 */
export type MfaChallengeMethod = 'totp' | 'otp';

/** What `POST /api/auth/mfa/verify` accepts as `method`. */
export type MfaVerifyMethod = MfaChallengeMethod | 'backup';

/** The runtime list of challenge methods — source for narrowing the login body. */
export const MFA_CHALLENGE_METHODS = ['totp', 'otp'] as const satisfies readonly MfaChallengeMethod[];

/**
 * The factors a user can *enrol* as second steps, per the manageable-methods
 * contract (`GET /api/auth/mfa/methods`). Distinct from `MfaChallengeMethod`:
 * enrolment names the persistent factor (`email`, `sms`, `line`) while the
 * challenge names the transport used at sign-in (`otp` covers any push channel).
 * `totp`, `email` and `sms` are wired; `line` may appear in `allowed` and
 * renders as "coming soon".
 */
export type MfaEnrollMethod = 'totp' | 'email' | 'sms' | 'line';

/**
 * The enrol methods that DELIVER a code somewhere. `totp` is generated on the
 * user's own device, so it is a factor but never a channel — that distinction is
 * what the challenge-screen picker and the "default channel" setting rest on.
 */
export type MfaOtpChannel = 'email' | 'sms' | 'line';

export const MFA_OTP_CHANNELS = ['email', 'sms', 'line'] as const satisfies readonly MfaOtpChannel[];

export function isMfaOtpChannel(value: unknown): value is MfaOtpChannel {
  return typeof value === 'string' && (MFA_OTP_CHANNELS as readonly string[]).includes(value);
}

/** Runtime list of enrolment methods — source for narrowing method payloads. */
export const MFA_ENROLL_METHODS = [
  'totp',
  'email',
  'sms',
  'line',
] as const satisfies readonly MfaEnrollMethod[];

/**
 * `GET /api/auth/mfa/methods` → the per-user factor set. `enrolled` is the
 * factors currently active (empty ⇒ 2FA off); `allowed` is every factor the
 * account is permitted to add. The security settings manager renders one row
 * per `allowed` method, badged when it also appears in `enrolled`.
 */
export interface IMfaMethodsState {
  enrolled: MfaEnrollMethod[];
  allowed: MfaEnrollMethod[];
  /**
   * The channel a sign-in challenge uses unless the user picks another. Absent when no channel
   * is enrolled, and from a backend that predates the SMS-channel amendment.
   */
  primaryOtpChannel?: MfaOtpChannel;
  /** `•••• 1234` — the stored number is never returned in full, so this is all there is to show. */
  maskedPhone?: string;
  /**
   * The engine's AUTHORITATIVE "is 2FA on", surfaced through the BFF. Deliberately separate from
   * `enrolled.length > 0`: a user who predates the method set has 2FA on with an EMPTY set, and
   * treating that as off drives the UI into an enrolment that calls `two-factor/enable` — which
   * mints a new secret and new backup codes and deletes the old row, breaking the authenticator
   * they actually use and the recovery codes they already saved.
   */
  twoFactorEnabled: boolean;
}

/** `POST /api/auth/mfa/enroll` → `200 {totpUri, backupCodes?}`. */
export interface IMfaEnrollment {
  /** `otpauth://totp/…` URI — rendered to a QR client-side, never sent anywhere. */
  totpUri: string;
  /**
   * One-time recovery codes, shown exactly once at enrollment. Present only when
   * this enrolment is the account's *first* factor; adding an authenticator to
   * an account that already has another factor reuses the existing secret and
   * returns no codes.
   */
  backupCodes?: string[];
}
